/* WeatherWarning (formerly HeatWarning) — dynamic weather card with
   mood-based illustration, copy and color palette.

   Moods derived from /api/weather payload:
     - hot:      temp >= 28 OR overall.level in {high, critical, source=heat}
     - cold:     temp <= 5
     - frozen:   temp <= 0
     - rain:     overall.source=='rain' OR humidity >= 88
     - cool:     5 < temp <= 12
     - mild:     comfortable (we hide the card)

   Each mood has its own Unsplash dog photo (sfumata), eyebrow, title and copy.
*/
import React, { useEffect, useState } from "react";
import { Thermometer, X, Snowflake, CloudRain, Sun, Wind } from "lucide-react";
import { api } from "@/lib/api";
import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";
import IlloImg from "@/components/IlloImg";

const DISMISS_KEY = "orio_weather_dismissed_until";

const MOODS = {
  frozen: {
    icon: Snowflake,
    accent: "#3B82F6",
    eyebrow: "Gelo",
    title: (t) => `${t}° · Attenzione zampe`,
    copy: "Sotto zero: limita la passeggiata, asciuga le zampe e attenzione al sale antighiaccio.",
    // Dog with red winter coat in the snow — intense cold
    photo: "weather-snow",
    bg: "linear-gradient(135deg, #E6F0FF 0%, #D6E4FB 60%, #C5D8F8 100%)",
  },
  cold: {
    icon: Snowflake,
    accent: "#5B6EE0",
    eyebrow: "Aria fredda",
    title: (t) => `${t}° · Vai con il cappottino`,
    copy: "Cani a pelo corto e cuccioli sentono il freddo: cappottino e passeggiate brevi.",
    // Dog wearing a sweater standing in the snow
    photo: "weather-cold",
    bg: "linear-gradient(135deg, #EAF1FB 0%, #E0E8F5 60%, #D2DCED 100%)",
  },
  rain: {
    icon: CloudRain,
    accent: "#2B7CE9",
    eyebrow: "Pioggia",
    title: () => "Sta piovendo · Impermeabile pronto",
    copy: "Porta un impermeabile e un asciugamano. Evita strade trafficate, l'asfalto bagnato è scivoloso.",
    // Woman with dog by water on a moody/grey day — rainy feel
    photo: "weather-rain",
    bg: "linear-gradient(135deg, #E5EEF7 0%, #D3E1F0 60%, #BFD3E8 100%)",
  },
  hot: {
    icon: Sun,
    accent: "#E94545",
    eyebrow: "Caldo",
    title: (t) => `${t}° · Cerca ombra e acqua`,
    copy: "Asfalto bollente, rischio colpo di calore. Esci all'alba o dopo il tramonto, sempre con acqua.",
    // Dog cools off in a lake on a sunny day — perfect for heat
    photo: "weather-hot",
    bg: "linear-gradient(135deg, #FFEFE0 0%, #FFD9B0 60%, #FFC380 100%)",
  },
  cool: {
    icon: Wind,
    accent: "#1E7E3F",
    eyebrow: "Fresco",
    title: (t) => `${t}° · Giornata perfetta`,
    copy: "Temperatura ideale per una bella passeggiata lunga. Goditi l'aria fresca con il tuo cane.",
    // Person walks their dog on a city street — ideal walking weather
    photo: "weather-wind",
    bg: "linear-gradient(135deg, #EAF5E7 0%, #D8EBD2 60%, #C2DDB9 100%)",
  },
  mild: {
    icon: Sun,
    accent: "#F4B83C",
    eyebrow: "Clima sereno",
    title: (t) => `${t}° · Passeggiata libera`,
    copy: "Clima ideale: porta acqua, fai sosta nei parchi e divertiti con il tuo cane.",
    // Person petting a happy dog outdoors in summer
    photo: "weather-mild",
    bg: "linear-gradient(135deg, #FFF8E5 0%, #FCEDC1 60%, #F8E0A0 100%)",
  },
};

function detectMood(data) {
  if (!data) return null;
  const t = Number(data.temp_c);
  if (Number.isNaN(t)) return null;
  const source = (data.overall?.source || "").toLowerCase();
  const level = data.overall?.level;
  if (source === "rain" || data.precipitation_mm > 0.2) return "rain";
  if (t <= 0) return "frozen";
  if (t <= 5) return "cold";
  if (t >= 28 || level === "critical" || level === "high" || source === "heat" || source === "pavement") return "hot";
  if (t <= 12) return "cool";
  if (data.humidity >= 88) return "rain";
  // Always show a card — even for mild weather (positive nudge to walk)
  return "mild";
}

export default function HeatWarning({ variant = "card" }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until > Date.now()) { setDismissed(true); return; }
    } catch {/* ignore */}

    const fetchWeather = async (lat, lng) => {
      try {
        const { data } = await api.get("/weather", { params: { lat, lng } });
        setData(data);
      } catch {/* silent */}
    };

    // Default fallback location: Rome (so the card always shows a real mood)
    const fallback = () => fetchWeather(41.9028, 12.4964);

    if (!navigator.geolocation) { fallback(); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => fetchWeather(p.coords.latitude, p.coords.longitude),
      () => fallback(),
      { timeout: 5000, maximumAge: 30 * 60 * 1000 }
    );
  }, []);

  if (!data || dismissed) return null;
  const mood = detectMood(data);
  if (!mood) return null;
  const m = MOODS[mood];
  const Icon = m.icon;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + 6 * 3600 * 1000)); } catch {/* ignore */}
    setDismissed(true);
  };

  if (variant === "compact") {
    return (
      <div
        data-testid="heat-warning-compact"
        className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 border bg-white shadow-[0_4px_12px_-4px_rgba(0,0,0,0.06)]"
        style={{ borderColor: `${m.accent}55` }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color: m.accent }} />
        <span className="text-[12px] font-medium" style={{ color: m.accent }}>{Math.round(data.temp_c)}° · {m.eyebrow}</span>
      </div>
    );
  }

  // Convert mood bg gradient last color to a solid for the fade overlay
  const fadeColor = m.bg.match(/#[0-9A-F]{6}/gi)?.[0] || "#FFFFFF";

  return (
    <div
      data-testid="heat-warning-card"
      data-weather-mood={mood}
      className="relative rounded-3xl border overflow-hidden active:scale-[0.985] transition-transform"
      style={{
        background: m.bg,
        borderColor: `${m.accent}44`,
        boxShadow: `0 8px 30px -10px ${m.accent}30, 0 2px 8px -2px rgba(0,0,0,0.04)`,
        minHeight: 220,
      }}
    >
      {/* Dismiss button */}
      <button
        onClick={dismiss}
        data-testid="heat-warning-dismiss"
        aria-label="Nascondi per 6 ore"
        className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/95 hover:bg-white flex items-center justify-center z-20 shadow-sm"
      >
        <X className="h-4 w-4 text-[#6B6B6B]" />
      </button>

      {/* Background illustration — IDENTICAL sizing to FeatureSlide carousel */}
      <div
        aria-hidden
        className="absolute -right-2 -bottom-2 w-[160px] h-[160px] sm:w-[180px] sm:h-[180px] pointer-events-none opacity-95 overflow-hidden"
        style={{
          WebkitMaskImage: "radial-gradient(circle at 65% 55%, #000 50%, transparent 78%)",
          maskImage: "radial-gradient(circle at 65% 55%, #000 50%, transparent 78%)",
        }}
      >
        <IlloImg slug={m.photo} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      </div>

      {/* Text content — IDENTICAL structure & padding to FeatureSlide (max-w-[65%]) */}
      <div className="relative z-10 p-5 max-w-[65%]">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: m.accent }}>
          <Icon className="h-3 w-3" />
          {m.eyebrow}
        </div>
        <h3 className="mt-2 font-display text-[17px] sm:text-[19px] leading-tight text-[#1A1A1A]">
          {m.title(Math.round(data.temp_c))}
        </h3>
        <p className="mt-2 text-[12.5px] text-[#5C5C5C] leading-snug">
          {data.overall?.advice || m.copy}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10.5px] text-[#6B6B6B]">
          <span className="inline-flex items-center gap-0.5"><Thermometer className="h-3 w-3" />{Math.round(data.temp_c)}°</span>
          {data.uv_index > 0 && <span className="inline-flex items-center gap-0.5"><Sun className="h-3 w-3" />UV{data.uv_index}</span>}
          {data.wind_kmh > 0 && <span className="inline-flex items-center gap-0.5"><Wind className="h-3 w-3" />{Math.round(data.wind_kmh)}km/h</span>}
          <span>{Math.round(data.humidity || 0)}%</span>
        </div>
      </div>
    </div>
  );
}
